import {Logger, Viewport} from '@mamdev/cad.static-next-lib';

import type {IAdData} from '../../../definitions';

export interface AdSize {
  width: string | undefined;
  height: string | undefined;
}

/**
 * Stores size of ad in global config
 * Run iframe adjustment just in case if ad.finish is not triggered later on
 */
export function handleAdResize(data: IAdData) {
  // store static ad size in object
  window.ui.triggeredAdSizes = window.ui.triggeredAdSizes ?? {};
  window.ui.triggeredAdSizes[data.slot.id] = {
    width: data.data.arguments[0].width,
    height: data.data.arguments[0].height
  };
  adjustIframe(data); // set iframe size
}

/** Adjust iframe size in ad container */
export function adjustIframe(data: IAdData) {
  try {
    const iframe = data.target?.querySelector('iframe');
    if (iframe) {
      setSize(iframe, {
        width: getIframeSize('width', data, iframe),
        height: getIframeSize('height', data, iframe)
      });
    }
  } catch (e) {
    Logger.logUnknownError(e, 'Adutils');
  }
}

/** expand ad, such as pushdown, floorad */
export function handleAddemandExpand(data: IAdData, windowInnerHeight: number) {
  if (data?.target) {
    const iframe = data.target.querySelector('iframe');

    if (iframe) {
      if (windowInnerHeight < 350) {
        // set alternative size of ad container for very small viewports
        setSize(iframe, {width: '350', height: '300'});
      } else {
        setSize(iframe, {
          width: data.data.width,
          height: data.data.height
        });
      }
    }
  }
}

/**
 * Custom tests to check if an ad slot is hidden (i.e. display "none") or not
 *
 * According to https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
 * an element's offsetParent property will return "null" when the element is not visible.
 * That's MUCH faster than calculating the computed style of the element.
 *
 * @param {HTMLElement} container The element to check
 * @return {boolean | undefined} false if the element is hidden, or undefined (?) if it's visible.
 */
export function isAdContainerDisplayed(container: HTMLElement): boolean | undefined {
  const parent = container.parentNode;
  let isVisible: boolean | undefined = undefined;

  if (parent !== document.body) {
    if (container.offsetParent === null) {
      isVisible = false;
    }
  } else if (window.getComputedStyle(container).display === 'none') {
    isVisible = false;
  }

  return isVisible;
}

/**
 * Calculate max width for specific positions
 * Mapping has been defined by TAM
 */
export function getMaxWidthParam(tagId: string, isInApp: boolean, offsetWidth: number, defaultMaxWidth: number | null): number {
  let maxWidth = 0;
  const vpw = offsetWidth;

  switch (tagId) {
    case 'top':
      if (!Viewport.matches('desktop')) {
        maxWidth = vpw;
      } else if ((Viewport.matches('desktop')) && (!Viewport.matches('large'))) {
        // 352 is special number from TAM (CADNPCA-300)
        maxWidth = vpw - 352;
      } else if (Viewport.matches('large')) {
        // 528 is special number from TAM (CADNPCA-300)
        maxWidth = vpw - 528;
      }
      // special case inapp
      if (isInApp) {
        maxWidth = vpw;
      }
      break;
    case 'right':
      if (Viewport.matches('large')) {
        // whatever (CADNPCA-300)
        maxWidth = vpw - 1080;
      }
      break;
    case 'skyByBillboard':
      if (Viewport.matches('large')) {
        // whatever again (CADNPCA-300)
        maxWidth = vpw - 1322;
      }
      break;
    case 'rectangle':
    case 'lcbox':
      maxWidth = 300;
      break;
    case 'contentad':
    default:
      if (defaultMaxWidth) {
        maxWidth = defaultMaxWidth;
      }
  }

  return maxWidth;
}

export function getAdViewport(clientWidth: number): number {
  const vw = clientWidth;

  if (vw < 480) {
    return 1;
  } else if (vw < 768) {
    return 2;
  } else if (vw < 1000) {
    return 3;
  } else if (vw < 1200) {
    return 4;
  } else {
    return 5;
  }
}

/**
 * Calculate layoutclass
 * b = big; m = medium; s = small
 * Mapping has been defined by TAM
 */
export function getLayoutClass(isInApp: boolean, offsetWidth: number): string {
  const width = getMaxWidthParam('top', isInApp, offsetWidth, null);
  if (Viewport.matches('tablet')) {
    if (width >= 895 && Viewport.matches('desktop') && !isInApp) {
      return 'b';
    }
    return 'm';
  }
  return 's';
}

/** Returns width or height to resize iframe */
function getIframeSize(dimension: 'width' | 'height', data: IAdData, iframe: HTMLIFrameElement): string | undefined {
  const slotId = data.slot.id;
  const adSizes = window.ui.triggeredAdSizes;
  const iframeOffsetDimension = dimension === 'width' ? iframe.offsetWidth : iframe.offsetHeight;

  // get size from adhook-trigger 'resize'
  if (adSizes?.[slotId]?.[dimension]) {
    return adSizes[slotId][dimension] as string;
  } else if (iframeOffsetDimension === 0 && data.slot._metaData[0][dimension]) {
    return data.slot._metaData[0][dimension]; // fallback to meta data adsize, only if dimension is zero
  }
  return undefined;
}

function setSize(element: HTMLElement, size: AdSize) {
  if (size.width) {
    setWidth(element, size.width);
  }
  if (size.height) {
    setHeight(element, size.height);
  }
}

function setHeight(element: HTMLElement, value: string | number) {
  element.style.height = fixSizeValue(value);
}

function setWidth(element: HTMLElement, value: string | number) {
  element.style.width = fixSizeValue(value);
}

/** Make sure that a unit of measure is specified for the specified value. */
function fixSizeValue(value: string | number): string {
  if (/\d$/.test(value.toString())) {
    // value ends with a digit = no unit specified, default to px
    return value + 'px';
  } else {
    return value.toString();
  }
}
